import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';

interface RefreshResult {
  status: string;
  message: string;
  details?: {
    ok: boolean;
    preserved_tables: { [key: string]: number };
    generation: number;
  };
}

export default function DatabaseManagementPage() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [result, setResult] = useState<RefreshResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setResult(null);
    setError(null);

    try {
      // Simple backend API call - no auth needed (internal tool)
      const response = await fetch('/api/database/refresh-qa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to refresh database');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon fontSize="large" />
          Database Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and refresh the FleetWatch QA database environment
        </Typography>
      </Box>

      {/* Main Action Card */}
      <Card sx={{ mb: 3, maxWidth: 800 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            QA Database Refresh
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Refresh the FleetWatch QA database from production while preserving identity data.
          </Typography>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            This operation will:
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Extract all identity schema tables (users, roles, accounts, etc.)</li>
              <li>Restore the QA database branch from production</li>
              <li>Re-insert preserved identity data</li>
              <li>Invalidate and clear all caches</li>
            </ul>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              {isRefreshing ? 'Refreshing Database...' : 'Refresh QA Database'}
            </Button>

            {isRefreshing && (
              <Typography variant="body2" color="text.secondary">
                This may take a few minutes...
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Success Result */}
      {result && result.status === 'success' && (
        <Card sx={{ maxWidth: 800, backgroundColor: '#f1f8f4' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" color="success.main">
                Database Refresh Completed
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary">
              The QA database has been successfully refreshed from production with all user data preserved.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card sx={{ maxWidth: 800, backgroundColor: '#fff4f4' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ErrorIcon color="error" />
              <Typography variant="h6" color="error">
                Database Refresh Failed
              </Typography>
            </Box>
            <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {error}
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

